import React, { useEffect, useRef, useState } from 'react';

/** Shown immediately, and if the stats API is unreachable. */
export const DEFAULT_USER_COUNT = 332454;

const COUNT_URLS = [
  '/api/app-users',
  'https://api.heypanda.org/stats/app-users.json',
];

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const ROLL_MS = 650;
const REFRESH_MS = 60 * 1000;

export function formatUserCount(n) {
  return Math.max(0, Math.floor(Number(n) || 0)).toLocaleString('en-US');
}

export function parseUserCount(data) {
  const n = Number(data && data.totalUsers);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.floor(n);
}

async function fetchLiveCount() {
  for (const url of COUNT_URLS) {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) continue;
      const count = parseUserCount(await res.json());
      if (count) return count;
    } catch {
      // try next source
    }
  }
  return null;
}

function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function DigitWheel({ digit }) {
  const value = Number(digit);
  const prev = useRef(value);
  const [offset, setOffset] = useState(value);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (value === prev.current) return undefined;

    if (value < prev.current) {
      setAnimate(true);
      setOffset(value + 10);
      const snap = setTimeout(() => {
        setAnimate(false);
        setOffset(value);
      }, ROLL_MS);
      prev.current = value;
      return () => clearTimeout(snap);
    }

    setAnimate(true);
    setOffset(value);
    prev.current = value;
    return undefined;
  }, [value]);

  return (
    <span className="digit-wheel" aria-hidden="true">
      <span
        className="digit-strip"
        style={{
          transform: `translateY(-${offset}em)`,
          transition: animate ? `transform ${ROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none',
        }}
      >
        {DIGITS.map((n, i) => (
          <span key={i} className="digit-num">{n}</span>
        ))}
      </span>
    </span>
  );
}

function RollingNumber({ value }) {
  const formatted = formatUserCount(value);
  return (
    <span className="trusted-by-count">
      {formatted.split('').map((ch, i) => (
        ch >= '0' && ch <= '9'
          ? <DigitWheel key={`d-${i}`} digit={ch} />
          : <span key={`s-${i}`} className="digit-sep">{ch}</span>
      ))}
    </span>
  );
}

export default function TrustedByCounter() {
  const [target, setTarget] = useState(DEFAULT_USER_COUNT);
  const [display, setDisplay] = useState(DEFAULT_USER_COUNT);
  const displayRef = useRef(DEFAULT_USER_COUNT);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const live = await fetchLiveCount();
      if (cancelled || !live) return;
      setTarget(live);
    };

    load();
    const refresh = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(refresh);
    };
  }, []);

  useEffect(() => {
    const from = displayRef.current;
    const to = target;
    if (from === to) return undefined;
    if (prefersReducedMotion()) {
      displayRef.current = to;
      setDisplay(to);
      return undefined;
    }

    const diff = to - from;
    const duration = Math.min(1800, 420 + Math.abs(diff) * 70);
    const start = performance.now();
    let frame;

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + diff * eased);
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  const labelCount = formatUserCount(display);

  return (
    <p className="trusted-by" aria-live="polite" aria-atomic="true">
      <span className="trusted-by-copy">Trusted by</span>
      <RollingNumber value={display} />
      <span className="trusted-by-copy">people</span>
      <span className="sr-only">{`Trusted by ${labelCount} people`}</span>
    </p>
  );
}
